"use client";

import { useEffect, useState } from "react";

type Props = {
  /** WhatsApp number (any format) — if absent the component renders nothing. */
  whatsapp?: string | null;
};

/**
 * Floating WhatsApp pill for mobile. Slides in after the hero, hides on
 * package-detail pages where MobileBookingBar already owns the thumb zone.
 */
export function MobileStickyCTA({ whatsapp }: Props) {
  const [visible, setVisible] = useState(false);
  const [suppressed, setSuppressed] = useState(false);

  useEffect(() => {
    const sync = () =>
      setSuppressed(document.body.classList.contains("has-trip-booking-bar"));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () =>
      setVisible(window.scrollY > window.innerHeight * 0.7);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const waDigits = whatsapp?.replace(/[^\d]/g, "") || null;
  if (!waDigits || suppressed) return null;

  return (
    <a
      href={`https://wa.me/${waDigits}`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      tabIndex={visible ? 0 : -1}
      className={`fixed bottom-5 right-4 z-40 lg:hidden flex items-center gap-1.5 rounded-full border border-white/10 bg-[rgba(8,17,30,0.72)] pr-0.5 pl-4 py-0.5 text-white shadow-[0_8px_32px_rgba(8,17,30,0.28)] backdrop-blur-md transition-all duration-300 ease-out active:scale-95 ${
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-14 opacity-0 pointer-events-none"
      }`}
    >
      <span className="text-[11px] font-semibold tracking-wide text-white/90">
        Chat with us
      </span>
      <span className="inline-grid size-6 shrink-0 place-items-center rounded-full bg-whatsapp">
        <svg height={14} width={14} aria-hidden fill="white">
          <use href="/icons.svg#whatsapp" />
        </svg>
      </span>
    </a>
  );
}
